import React, { useState } from "react";
import { MInputField } from "@/components/ui/atoms/MInputField";

import { MRelationship } from "@/components/ui/molecules/MRelationship";
import { MDialCode } from "@/components/ui/molecules/MDialCode";

import {
  relationshipOptionsForEmergency,
  RelationshipType,
} from "@/types/relationship";
import { IconCaretDownFilled } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";

export type EmergencyContactValue = {
  name: string;
  relationship?: RelationshipType;
  otherRelation?: string;
  dialCode: string;
  mobile: string;
};

interface ContactEmergencyProps {
  value: EmergencyContactValue;
  onChange: (val: EmergencyContactValue) => void;
  error?: string;
  success?: boolean;
  disabled?: boolean;
  showRelationship?: boolean;
  isPet?: boolean;
  contactType?: "emergency" | "doctor"; // default is 'emergency'
}

const ContactEmergency: React.FC<ContactEmergencyProps> = ({
  value,
  onChange,
  error,
  success,
  disabled,
  showRelationship = true,
  isPet = false,
  contactType = "emergency",
}) => {
  const { t } = useTranslation();
  const [showRelationshipDrawer, setShowRelationshipDrawer] = useState(false);
  const [showDialCodePicker, setShowDialCodePicker] = useState(false);

  const relationshipLabel =
    relationshipOptionsForEmergency.find(
      (opt) => opt.value === value.relationship
    )?.label || "";

  return (
    <div className="flex flex-col gap-[var(--scale-padding-4xl)] overflow-visible w-full">
      <div className="w-full">
        <MInputField
          label={
            contactType === "doctor"
              ? isPet
                ? t("contact.vetName")
                : t("contact.doctorsName")
              : t("contact.emergencyContact")
          }
          value={value.name}
          onChange={(e) => onChange({ ...value, name: e.target.value })}
          placeholder="Not Answered"
          variant="Underlined"
          state={error ? "Error" : success ? "Success" : "Default"}
          size="md"
          showLabel
          disabled={disabled}
        />
      </div>
      {showRelationship && (
        <>
          <div
            className="w-full"
            onClick={() => !disabled && setShowRelationshipDrawer(true)}
          >
            <MInputField
              label={t("contact.relationshipWithContact")}
              value={relationshipLabel}
              placeholder="Not Answered"
              variant="Underlined"
              size="md"
              showLabel
              disabled={disabled}
              icon={<IconCaretDownFilled />}
              showIcon={true}
            />
          </div>
          {value.relationship === 7 && (
            <div className="w-full">
              <MInputField
                label="What's your relationship?"
                value={value.otherRelation || ""}
                onChange={(e) =>
                  onChange({ ...value, otherRelation: e.target.value })
                }
                placeholder="Not Answered"
                variant="Underlined"
                size="md"
                showLabel
                disabled={disabled}
                maxLength={30}
              />
            </div>
          )}
        </>
      )}
      <div className="flex gap-2 items-end w-full">
        <div className="flex-shrink-0" style={{ width: 54 }}>
          <MInputField
            showLabel
            label={t("common.mobileNumber")}
            value={value.dialCode}
            className="cursor-pointer text-base dialcode-input"
            variant="Underlined"
            size="md"
            disabled={disabled}
          />
        </div>
        <div className="flex-1">
          <MInputField
            value={value.mobile}
            inputMode="numeric"
            onChange={(e) => onChange({ ...value, mobile: e.target.value })}
            placeholder={t("common.mobileNumber")}
            variant="Underlined"
            size="md"
            type="number"
            disabled={disabled}
            maxLength={10}
          />
        </div>
      </div>
      <MDialCode
        open={showDialCodePicker}
        onClose={() => setShowDialCodePicker(false)}
        onDone={(code) => {
          onChange({ ...value, dialCode: code });
          setShowDialCodePicker(false);
        }}
        initialValue={value.dialCode}
      />
      {showRelationship && (
        <MRelationship
          open={showRelationshipDrawer}
          onClose={() => setShowRelationshipDrawer(false)}
          onDone={(rel) => {
            onChange({ ...value, relationship: rel });
            setShowRelationshipDrawer(false);
          }}
          initialValue={value.relationship}
          options={relationshipOptionsForEmergency}
        />
      )}
      {/* Success and error states are now handled via the state prop on MInputField */}
    </div>
  );
};

export default ContactEmergency;
